import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import useViewAs from './useViewAs';
import {
  Box, AppBar, Toolbar, Avatar, Typography, Stack, Chip,
  IconButton, Tooltip, Container, Button,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Logout as LogoutIcon,
  AutoStories as PortalIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

// TeacherPortalLayout — minimal, polished chrome around the teacher's
// score-entry experience. No sidebar, no module menu. The viewport
// gets a soft gradient backdrop with a breathing-room Container so
// nothing ever feels glued to the edge of the screen.
export default function TeacherPortalLayout() {
  const theme = useTheme();
  const nav = useNavigate();
  const loc = useLocation();
  const { user, logout } = useAuth();
  const p  = theme.palette.primary.main;
  const p2 = theme.palette.secondary.main;
  const initials = (user?.full_name || user?.email || '?').split(/\s+/)
    .map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  const handleLogout = () => { logout(); nav('/login', { replace: true }); };
  // Going "back to menu" during an admin preview has to keep carrying
  // ?view_as — TeacherGuard only lets a non-teacher stay inside the
  // portal while it is there, so dropping it lands the admin on
  // /dashboard instead of the hub. See useViewAs.js.
  const { withViewAs } = useViewAs();
  const backToMenu = () => nav(withViewAs('/teacher-portal'));
  // Show a Back-to-menu button whenever the teacher is deeper than the
  // hub (any /teacher-portal/<something> path). Trailing slash normalised.
  const inSubPage = /^\/teacher-portal\/[^/]+/.test(loc.pathname.replace(/\/+$/, ''));

  // Routes that carry the teacher's identity in their own toolbar and
  // therefore do not want this banner as well. Kept as an explicit list
  // rather than a flag passed through the Outlet, because the layout has
  // to decide before the child renders.
  const BANNER_FREE = [/^\/teacher-portal\/collections(\/|$)/];
  const hideBanner = BANNER_FREE.some(re => re.test(loc.pathname.replace(/\/+$/, '') || '/'));

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      // Soft layered gradient — gives the whole app a calmer, modern
      // backdrop instead of the flat grey we had before.
      background: `
        radial-gradient(at 0% 0%,   rgba(94,124,255,0.10) 0%, transparent 55%),
        radial-gradient(at 100% 0%, rgba(245,108,170,0.08) 0%, transparent 55%),
        linear-gradient(180deg, #f3f5fb 0%, #eef1f8 100%)
      `,
    }}>
      {/* Slim top bar */}
      <AppBar position="sticky" color="default" elevation={0}
        sx={{
          bgcolor: 'rgba(255,255,255,0.85)',
          backdropFilter: 'saturate(180%) blur(10px)',
          borderBottom: '1px solid', borderColor: 'divider',
        }}>
        <Toolbar sx={{ minHeight: '52px !important', px: { xs: 1, sm: 1.5, md: 3 }, gap: 0.5 }}>
          {/* On a phone the back arrow comes FIRST and is icon-only —
              it is the control teachers reach for most, and the full
              "Back to menu" button plus the title would not fit on a
              360px screen without the whole bar wrapping. */}
          {inSubPage && (
            <Tooltip title="Back to menu">
              <IconButton size="small" color="primary"
                onClick={backToMenu}
                sx={{ display: { xs: 'inline-flex', sm: 'none' }, mr: 0.5 }}>
                <BackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <PortalIcon sx={{ color: 'primary.main', mr: 1, display: { xs: 'none', sm: 'block' } }} />
          <Typography noWrap sx={{
            fontWeight: 800, fontSize: { xs: 13, sm: 14 },
            letterSpacing: 0.4, color: 'primary.main', minWidth: 0,
          }}>
            Teacher Portal
          </Typography>
          {inSubPage && (
            <Button size="small" color="primary" variant="outlined"
              startIcon={<BackIcon fontSize="small" />}
              onClick={backToMenu}
              sx={{
                ml: 2, fontWeight: 700, textTransform: 'none',
                display: { xs: 'none', sm: 'inline-flex' },
              }}>
              Back to menu
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Sign out">
            <IconButton onClick={handleLogout} size="small" color="primary">
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* Breathing-room container — full width with a small side gutter
          so the roster tables can fit more columns without wrapping.
          xl (1536px) was leaving a lot of wasted space on wide displays. */}
      <Container maxWidth={false} sx={{
        flex: 1, display: 'flex', flexDirection: 'column',
        px: { xs: 1.6, sm: 2.5, md: 3 },
        py: { xs: 1.2, md: 1.6 },
      }}>
        {/* Screens that fold the teacher's identity into their own
            single toolbar suppress this banner entirely — stacking both
            would put two identity strips on one page. The day sheet is
            the case: its whole job is a long roster, and every row of
            chrome above it is a row of learners pushed off screen. */}
        {!hideBanner && (
        <>
        {/* Teacher banner — one compact line.
            This was a tall hero block: a 70px avatar, a 30px name and the
            email on its own row, roughly 150px of chrome before any work
            appeared. On the day sheet that pushed the roster below the
            fold on a laptop, and the name is not information a teacher
            needs re-reading all day. Everything now sits on a single
            line about half the height, and the whole banner collapses on
            the roster screens where space matters most. */}
        <Box sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 2,
          mb: { xs: 1.2, md: 1.6 },
          px: { xs: 1.6, md: 2.2 },
          py: { xs: 1, md: 1.15 },
          background: `linear-gradient(135deg, ${p2} 0%, ${p} 100%)`,
          color: '#fff',
          boxShadow: '0 8px 18px -12px rgba(26,35,126,0.4)',
        }}>
          {/* Soft blob in the corner — adds depth without distraction */}
          <Box sx={{
            position: 'absolute', right: -50, top: -70,
            width: 150, height: 150, borderRadius: '50%',
            background: 'rgba(255,255,255,0.10)',
          }} />
          <Stack direction="row" alignItems="center" spacing={1.3}
            sx={{ position: 'relative' }}>
            <Avatar sx={{
              width: { xs: 34, md: 38 }, height: { xs: 34, md: 38 },
              bgcolor: 'rgba(255,255,255,0.22)',
              border: '2px solid rgba(255,255,255,0.5)',
              fontWeight: 800, fontSize: { xs: 13, md: 14.5 },
            }}>
              {initials}
            </Avatar>
            <Typography noWrap sx={{
              color: '#fff', fontWeight: 900, lineHeight: 1.2,
              fontSize: { xs: 15.5, sm: 17, md: 18.5 },
              textShadow: '0 1px 2px rgba(0,0,0,0.18)',
              minWidth: 0,
            }}>
              {user?.full_name || user?.email || 'Teacher'}
            </Typography>
            <Chip size="small" label={user?.role_label || 'Teacher'}
              sx={{
                bgcolor: 'rgba(255,255,255,0.22)', color: '#fff',
                fontWeight: 700, height: 20, fontSize: 10.5,
                letterSpacing: 0.4, textTransform: 'uppercase',
              }} />
            <Box sx={{ flex: 1 }} />
            {user?.email && (
              <Typography noWrap sx={{
                color: 'rgba(255,255,255,0.85)', fontSize: 11.5,
                display: { xs: 'none', md: 'block' }, maxWidth: 260,
              }}>
                {user.email}
              </Typography>
            )}
          </Stack>
        </Box>
        </>
        )}

        <Box sx={{ flex: 1 }}>
          <Outlet />
        </Box>
      </Container>
    </Box>
  );
}
